/* eslint-disable complexity */
import { Divider, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import { useEffect, useState } from 'react';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { SmallCard } from 'theme/elements/Cards';
import { useNavigate, useParams } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { getCompanyDashboard } from 'state/reducers/customer-accounts.reducers';
import { formatDate, formatNumber } from 'constants/global.constants';
import { CarouselComponent } from 'components/Carousel';
import { customerAccountDetailsTypes, moneyActivityTypes } from 'constants/customer-account.constants';
import { SwiperSlide } from 'swiper/react';
import { CustomCompanySwitch, RiskLevel } from './elements';

export const UpperDashboard = () => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const navigate = useNavigate();
	const { id } = useParams();
	const companyData = useAppSelector(getCompanyDashboard);
	const [balanceSlides, setBalancesSlides] = useState<any>(null);
	const pagePrefix = 'page.customerAccounts.';
	const basicDataPrefix = `${pagePrefix}basicData.`;
	const [lastApprovedPayment, setLastApprovedPayment] = useState<any>(null);

	useEffect(() => {
		if (companyData) {
			let gradientInitialPercent = 100;
			const cards = companyData?.balances?.map((item: any, index: number) => {
				const { iban } = item;
				const splitNumber = formatNumber(item?.amount);
				const splitPending = formatNumber(item?.pendingAmount);
				const gradientPercent = gradientInitialPercent;
				gradientInitialPercent = gradientInitialPercent === 70 ? 100 : gradientInitialPercent - 10;
				const card = (
					<SwiperSlide
						key={index}
						style={{
							maxWidth: '229px !important',
							width: '229px !important',
							maxHeight: '139px',
							height: '139px',
						}}
					>
						<Grid
							item
							xs={8}
							sm={'auto'}
							key={index}
							sx={{
								overflow: 'hidden',
								height: '100%',
								width: '100%',
								transform: 'scalex(-1)',
								direction: 'rtl',
							}}
						>
							<SmallCard
								key={index}
								styleObj={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
									background: `linear-gradient(91deg, rgb(20 76 71 / ${gradientPercent}%) 100%, ${theme.palette.root.basicDeeoGreen} 100%)`,
									color: 'white',
									position: 'relative',
									height: '100%',
									width: '100%',
								}}
							>
								<Stack spacing={2} width="100%">
									<Stack
										direction="row"
										alignItems="baseline"
										sx={{ transform: 'scalex(-1)', justifyContent: 'flex-end' }}
									>
										<Typography variant="h4" px={1}>
											{item?.currency?.symbol}
											{item?.currency?.code}
										</Typography>
										<Typography variant="boldBody3" sx={{ color: theme.palette.root.orange_3 }}>
											{parseInt(splitNumber[0]).toLocaleString()}
										</Typography>
										<Typography variant="boldBody5" sx={{ color: theme.palette.root.orange_3 }}>
											.{splitNumber[1]}
										</Typography>
									</Stack>
									<Stack
										spacing={2}
										sx={{
											transform: 'scalex(-1)',
											alignItems: 'end',
											marginTop: '12px !important',
										}}
									>
										<Typography
											variant="body"
											sx={{ marginTop: '0px !important', lineHeight: '12px' }}
										>
											{t('page.customerAccounts.customerBalance.balanceInProgress')}
										</Typography>
										<Stack direction="row" sx={{ marginTop: '12px !important' }}>
											<Typography variant="boldBody6">
												{item?.pendingAmount < 0 && '-'}
												{item?.currency?.symbol}
												{item?.pendingAmount !== 0
													? Math.abs(parseInt(splitPending[0])).toLocaleString()
													: '0'}
											</Typography>
											<Typography variant="body1">.{splitPending[1] || '00'}</Typography>
										</Stack>
									</Stack>
									{iban && (
										<Stack
											direction={'row'}
											sx={{
												transform: 'scaleX(-1)',
												justifyContent: 'flex-end',
												marginTop: '4px !important',
											}}
										>
											<Stack direction={'row'} marginLeft="4px">
												<Typography
													variant="body5"
													sx={{ fontSize: '11px !important', marginTop: '0px !important' }}
												>
													{t('page.customerAccounts.customerBalance.iban') + ':'}
												</Typography>
												<Typography
													variant="body5"
													sx={{
														textDecoration: 'underline',
														fontSize: '11px !important',
														marginTop: '0px !important',
													}}
												>
													{iban}
												</Typography>
											</Stack>

											<img
												src={'/icons/copy.svg'}
												style={{ filter: 'brightness(0) invert(1)', cursor: 'pointer' }}
												onClick={() => navigator.clipboard.writeText(iban)}
											/>
										</Stack>
									)}
								</Stack>
								<img
									src="/icons/logo-brand-icon.svg"
									style={{
										filter: 'brightness(0) invert(1)',
										opacity: '0.3',
										position: 'absolute',
										right: '3%',
										bottom: '10%',
										transform: 'scalex(-1)',
									}}
								/>
							</SmallCard>
						</Grid>
					</SwiperSlide>
				);
				return card;
			});
			setBalancesSlides(cards);
			setLastApprovedPayment(companyData?.lastCompletedPayment);
		}
	}, [companyData]);

	return (
		<Stack sx={{ marginTop: '-10px' }}>
			{companyData && (
				<>
					<Stack spacing={4}>
						<Stack direction="row" alignItems="center" spacing={4}>
							<Typography
								onClick={() =>
									navigate(
										`/${pagesEnum.CUSTOMER_ACCOUNTS_DETAILS}/${id}/${customerAccountDetailsTypes.BASIC_DATA}`
									)
								}
								variant="boldBody3"
								sx={{ color: theme.palette.common.black, '&:hover': { cursor: 'pointer' } }}
							>
								{companyData?.basicData?.hebrewName || companyData?.basicData?.name}
							</Typography>
							<CustomCompanySwitch closed={Boolean(companyData?.basicData?.closedByBoUserId)} />
						</Stack>
						<Stack spacing={1}>
							<Typography
								variant="h4"
								sx={{ color: theme.palette.common.black, fontWeight: 700, marginTop: '-8px' }}
							>
								{companyData?.basicData?.englishName}
							</Typography>
							<Typography variant="subtitle" sx={{ color: theme.palette.root.grey }}>
								{companyData?.basicData?.customerType
									? t(`${basicDataPrefix}accountType.${companyData?.basicData?.customerType}`)
									: t(`${basicDataPrefix}accountType.unknownCustomerType`)}
								, {'  '}
								{companyData?.basicData?.companyIdentityNumber}
							</Typography>
						</Stack>
						<Grid
							container
							spacing={8}
							columns={4}
							sx={{
								margin: '-30px auto auto -65px!important',
								color: theme.palette.root.formTitle,
								textAlign: 'center',
							}}
						>
							<Grid item xs="auto">
								<Stack spacing={1}>
									<Typography variant="h4" sx={{ fontWeight: 700 }}>
										{companyData?.basicData?.accountId}
									</Typography>
									<Typography variant="body">{t(`${basicDataPrefix}openTitle`)}</Typography>
								</Stack>
							</Grid>
							<Grid item xs="auto">
								<Stack spacing={1}>
									<Typography variant="h4" sx={{ fontWeight: 700 }}>
										{companyData?.basicData?.ccId}
									</Typography>
									<Typography variant="body">{t(`${basicDataPrefix}cc`)}</Typography>
								</Stack>
							</Grid>
							<Grid item xs="auto">
								<Stack spacing={1}>
									<Typography variant="h4" sx={{ fontWeight: 700 }}>
										{companyData?.basicData?.hyperAccountNumber ||
											t(`${pagePrefix}generalLabel.unKnown`)}
									</Typography>
									<Typography variant="body">{t(`${basicDataPrefix}hyper`)}</Typography>
								</Stack>
							</Grid>
							<Grid item xs="auto">
								<RiskLevel riskLevel={companyData?.basicData?.riskLevel} />
							</Grid>
						</Grid>
						<Grid
							container
							spacing={8}
							columns={4}
							sx={{
								margin: '-30px auto auto -65px!important',
								color: theme.palette.root.formTitle,
								textAlign: 'center',
							}}
						>
							<Grid item xs="auto">
								<Stack spacing={1.5}>
									<Typography
										variant="h4"
										sx={{
											fontWeight: 700,
											textDecoration: 'underline',
											textUnderlineOffset: '5px',
										}}
									>
										{companyData?.basicData?.manager?.name ||
											t(`${basicDataPrefix}altshulerEmployeeUnknown`)}
									</Typography>
									<Typography variant="body">{t(`${basicDataPrefix}manager`)}</Typography>
								</Stack>
							</Grid>
							<Grid item xs="auto">
								<Stack spacing={1.5}>
									<Typography
										variant="h4"
										sx={{
											fontWeight: 700,
											textDecoration: 'underline',
											textUnderlineOffset: '5px',
										}}
									>
										{companyData?.basicData?.dealer?.name ||
											t(`${basicDataPrefix}altshulerEmployeeUnknown`)}
									</Typography>
									<Typography variant="body">{t(`${basicDataPrefix}dealer`)}</Typography>
								</Stack>
							</Grid>
							<Grid item xs="auto">
								<Stack spacing={1.5}>
									<Typography
										variant="h4"
										sx={{
											fontWeight: 700,
											textDecoration: 'underline',
											textUnderlineOffset: '5px',
										}}
									>
										{companyData?.basicData?.agent?.name ||
											t(`${basicDataPrefix}altshulerEmployeeUnknown`)}
									</Typography>
									<Typography variant="body">{t(`${basicDataPrefix}agent`)}</Typography>
								</Stack>
							</Grid>
							<Grid item xs="auto">
								<Stack spacing={1.5}>
									<Typography
										variant="h4"
										sx={{
											fontWeight: 700,
											textDecoration: 'underline',
											textUnderlineOffset: '5px',
										}}
									>
										{companyData?.basicData?.lead?.name ||
											t(`${basicDataPrefix}altshulerEmployeeUnknown`)}
									</Typography>
									<Typography variant="body">{t(`${basicDataPrefix}lead`)}</Typography>
								</Stack>
							</Grid>
						</Grid>
						<Grid container spacing={4} columns={5} sx={{ margin: '0 auto auto -32px!important' }}>
							<Grid item xs={8} sm={'auto'} sx={{ cursor: 'pointer' }}>
								<SmallCard
									styleObj={{
										width: '270px',
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'space-between',
									}}
									handleClick={() =>
										navigate(`/${pagesEnum.PENDING_REQUESTS}/all/${companyData?.basicData?.id}`)
									}
								>
									<Stack spacing={1} sx={{ marginTop: 2 }}>
										<img src="/icons/hourglass.svg" style={{ width: '24px', height: '24' }} />
										<Typography variant="boldBody4">
											{companyData?.pendingRequests?.total}
											{'  '}
											{t('page.customerAccounts.pendingRequests')}
										</Typography>
									</Stack>
									<img src="/icons/arrow-left.svg" style={{ marginRight: '10px' }} />
								</SmallCard>
							</Grid>
							<Grid item xs={8} sm={'auto'} sx={{ cursor: 'pointer' }}>
								<SmallCard
									styleObj={{
										width: '270px',
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'space-between',
									}}
									handleClick={() =>
										lastApprovedPayment?.id &&
										navigate(
											`/${pagesEnum.CUSTOMER_ACCOUNTS_DETAILS}/${id}/${moneyActivityTypes.PAYMENT}/${lastApprovedPayment.id}`
										)
									}
								>
									<Stack spacing={1} sx={{ marginTop: 2 }}>
										<img src="/icons/hourglass.svg" style={{ width: '24px', height: '24' }} />
										<Typography variant="boldBody4">
											{t('page.customerAccounts.lastApprovedPayment')}:{'  '}
											{lastApprovedPayment
												? formatDate(lastApprovedPayment?.updatedAt)
												: t('page.customerAccounts.lastApprovedPayment.none')}
										</Typography>
									</Stack>
									<img src="/icons/arrow-left.svg" style={{ marginRight: '10px' }} />
								</SmallCard>
							</Grid>

							<Grid item xs={8} sm={'auto'} sx={{ cursor: 'pointer' }}>
								<SmallCard
									styleObj={{
										width: '270px',
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'space-between',
									}}
									handleClick={() =>
										navigate(
											`/${pagesEnum.CUSTOMER_ACCOUNTS_DETAILS}/${id}/${customerAccountDetailsTypes.PLAN}`
										)
									}
								>
									<Stack spacing={1} sx={{ marginTop: 2 }}>
										<img src="/icons/hourglass.svg" style={{ width: '24px', height: '24' }} />
										<Typography variant="boldBody4">
											{t('page.customerAccounts.plan')}:{'  '}
											<span style={{ fontWeight: 700 }}>{companyData?.plan?.plan?.name}</span>
										</Typography>
									</Stack>
									<img src="/icons/arrow-left.svg" style={{ marginRight: '10px' }} />
								</SmallCard>
							</Grid>
						</Grid>
						<Divider
							orientation="horizontal"
							flexItem
							sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
						/>
						<Stack spacing={1} sx={{ position: 'relative' }}>
							<Typography variant="h4" sx={{ color: theme.palette.common.black, fontWeight: 700 }}>
								{t(`${pagePrefix}customerBalance.title`)}
							</Typography>
							{balanceSlides?.length > 0 ? (
								<CarouselComponent>{balanceSlides}</CarouselComponent>
							) : (
								t(`${pagePrefix}customerBalance.noBalances`)
							)}
						</Stack>
					</Stack>
				</>
			)}
		</Stack>
	);
};
